import { useCallback } from 'react';

import { translationKey, type TranslationKey } from '@change-corgi/core/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useStartAPetitionPageState } from 'src/app/pages/startAPetitionV2/pageContext';

type Placement = 'top' | 'bottom';

type UseBannerActionsReturn = ModelHookResult<
	{
		draftModeTranslationValue: TranslationKey;
	},
	{ transitionToPreview: (placement: Placement) => void; toggleDraftMode: (placement: Placement) => void }
>;

type UseBannerActionsParams = { onPrimaryAction: () => void };

// eslint-disable-next-line max-lines-per-function
export function useBannerActions({ onPrimaryAction }: UseBannerActionsParams): UseBannerActionsReturn {
	const { actions, data } = useStartAPetitionPageState();
	const track = useTracking();

	const draftMode = data.draft?.metadata?.draftMode || 'recommended_draft';

	const transitionToPreview = useCallback(
		(placement: Placement) => {
			void track('sap_preview_click', { placement });
			onPrimaryAction();
		},
		[onPrimaryAction, track],
	);

	const activateRecommendedDraft = useCallback(() => {
		const recommendedDraft = data.draft?.recommendedDraft || {};
		actions.send({
			type: 'submit',
			target: 'self',
			draft: { petitionDraft: { ...recommendedDraft }, metadata: { draftMode: 'recommended_draft' } },
		});
	}, [actions, data.draft?.recommendedDraft]);

	const activateUserDraft = useCallback(() => {
		actions.send({
			type: 'submit',
			target: 'self',
			draft: { petitionDraft: { ask: '', description: '', image: null }, metadata: { draftMode: 'user_draft' } },
		});
	}, [actions]);

	const toggleDraftMode = useCallback(
		(placement: Placement) => {
			if (draftMode === 'recommended_draft') {
				void track('sap_clear_draft_click', { placement, context: 'edit' });
				activateUserDraft();
			} else {
				void track('sap_back_to_draft_click', { placement, context: 'edit' });
				activateRecommendedDraft();
			}
		},
		[activateRecommendedDraft, activateUserDraft, draftMode, track],
	);

	const draftModeTranslationValue =
		draftMode === 'recommended_draft'
			? translationKey('corgi.start_a_petition.edit.banner_actions.action_buttons.scratch')
			: translationKey('corgi.start_a_petition.edit.banner_actions.action_buttons.recommended');

	return {
		data: {
			draftModeTranslationValue,
		},
		actions: {
			transitionToPreview,
			toggleDraftMode,
		},
	};
}
